import React, { useEffect, useState } from 'react';
import { termsApi } from '../lib/api';
import { TermsModal } from './TermsModal';
import { useAuthStore } from '../store/authStore';
import type { TermsOfService } from '../types/api';
import { notify } from '../store/notificationStore';

export const TermsChecker: React.FC = () => {
  const { isAuthenticated, user } = useAuthStore();
  const [showModal, setShowModal] = useState(false);
  const [currentTerms, setCurrentTerms] = useState<TermsOfService | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkTermsStatus = async () => {
      // Only check if user is authenticated
      if (!isAuthenticated || !user) {
        setIsChecking(false);
        return;
      }

      try {
        // Check if user needs to accept new terms
        const status = await termsApi.getStatus();

        // If user is exempt (staff/admin) or doesn't need to accept, we're done
        if (status.is_exempt || !status.needs_to_accept_terms) {
          setIsChecking(false);
          return;
        }

        // Fetch current terms if user needs to accept
        const terms = await termsApi.getCurrent();
        setCurrentTerms(terms);
        setShowModal(true);
      } catch (error) {
        console.error('Failed to check terms status:', error);
        // Don't block app usage if terms check fails
      } finally {
        setIsChecking(false);
      }
    };

    checkTermsStatus();
  }, [isAuthenticated, user]);

  const handleAcceptTerms = async () => {
    setIsAccepting(true);

    try {
      const response = await termsApi.accept();
      notify.success('Terms Accepted', `You have accepted Terms of Service v${response.version}`);
      setShowModal(false);
    } catch (error) {
      console.error('Failed to accept terms:', error);
      const errorObj = error as Record<string, unknown>;
      const errorMessage = (errorObj?.error as Record<string, unknown>)?.message;
      notify.error(
        'Failed to Accept Terms',
        typeof errorMessage === 'string'
          ? errorMessage
          : 'Unable to accept terms. Please try again.'
      );
    } finally {
      setIsAccepting(false);
    }
  };

  // Don't render anything if not checking or modal not needed
  if (!showModal || isChecking) {
    return null;
  }

  return (
    <TermsModal
      isOpen={showModal}
      onAccept={handleAcceptTerms}
      terms={currentTerms}
      isAcceptMode={true}
      isLoading={isAccepting}
      canClose={false} // Force user to accept
    />
  );
};
